import { addDays, format, parseISO, isSameDay, isAfter } from 'date-fns';
import { supabase } from './supabaseClient';

export interface DeliverySlot {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  order_deadline_days?: number; // Days before delivery for order cutoff
  order_deadline_time?: string; // Time of day for order cutoff (HH:MM)
}

export interface DeliveryDateOption {
  date: Date;
  slots: DeliverySlot[];
}

/**
 * Syncs delivery start date from delivery_settings to app_settings
 * This ensures both tables have consistent data
 */
export async function syncDeliveryStartDate(): Promise<void> {
  try {
    // Get the current value from delivery_settings
    const { data: deliveryData, error: deliveryError } = await supabase
      .from('delivery_settings')
      .select('start_date')
      .eq('id', 'default')
      .single();

    if (deliveryError || !deliveryData?.start_date) {
      console.warn('Could not sync delivery start date - no data in delivery_settings');
      return;
    }

    // Update app_settings with this value
    const { error: updateError } = await supabase
      .from('app_settings')
      .upsert({
        key: 'delivery_start_date',
        value: deliveryData.start_date,
        description: 'Start date for delivery calculations'
      });

    if (updateError) {
      console.error('Error syncing delivery start date to app_settings:', updateError);
    } else {
      console.log('Successfully synced delivery start date to app_settings');
    }
  } catch (error) {
    console.error('Exception in syncDeliveryStartDate:', error);
  }
}

/**
 * Fetches the delivery start date from the database
 * @returns Promise with the delivery start date or null if not found
 */
export async function fetchDeliveryStartDate(): Promise<Date | null> {
  try {
    console.log("Fetching delivery start date from database");
    
    // First try to get from app_settings table
    const { data: settingsData, error: settingsError } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'delivery_start_date')
      .maybeSingle(); // Use maybeSingle() to avoid errors when no data is found

    if (!settingsError && settingsData && settingsData.value) {
      console.log("Delivery start date from app_settings:", settingsData.value);
      return parseISO(settingsData.value);
    }

    // If not found in app_settings, try delivery_settings table (legacy)
    const { data, error } = await supabase
      .from('delivery_settings')
      .select('start_date')
      .eq('id', 'default')
      .maybeSingle(); // Use maybeSingle() to avoid errors when no data is found

    if (error) {
      console.warn('delivery_settings table not found or error:', error.message);
      // Return tomorrow as default start date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      console.log("Using default start date (tomorrow):", tomorrow.toISOString());
      return tomorrow;
    }
    
    if (data && data.start_date) {
      console.log("Delivery start date from delivery_settings:", data.start_date);
      
      // Sync this value to app_settings for future use
      await syncDeliveryStartDate();
      
      return parseISO(data.start_date);
    }
    
    console.warn("No delivery start date found in database, using default");
    // Return tomorrow as default
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  } catch (error) {
    console.error('Exception in fetchDeliveryStartDate:', error);
    // Return tomorrow as fallback
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    console.log("Using fallback start date (tomorrow):", tomorrow.toISOString());
    return tomorrow;
  }
}

/**
 * Fetches active delivery slots from the database
 * @returns Promise with the delivery slots or empty array if none found
 */
export async function fetchDeliverySlots(): Promise<DeliverySlot[]> {
  try {
    console.log("Fetching delivery slots from database");
    const { data, error } = await supabase
      .from('delivery_slots')
      .select('*')
      .eq('is_active', true)
      .order('day_of_week')
      .order('start_time');

    if (error) {
      console.error('Error fetching delivery slots:', error);
      throw new Error('Failed to fetch delivery slots');
    }

    console.log(`Fetched ${data?.length || 0} active delivery slots`);
    return data || [];
  } catch (error) {
    console.error('Exception in fetchDeliverySlots:', error);
    throw error;
  }
}

/**
 * Calculates available delivery dates based on delivery settings and slots
 * @param deliveryStartDate The configured start date from database
 * @param deliverySlots The available delivery slots
 * @returns Array of available delivery dates with their slots
 */
export function calculateAvailableDeliveryDates(
  deliveryStartDate: Date | null,
  deliverySlots: DeliverySlot[]
): DeliveryDateOption[] {
  const dates: DeliveryDateOption[] = [];
  const today = new Date();
  const now = new Date(); // Current time for deadline checks
  today.setHours(0, 0, 0, 0); // Normalize time to beginning of day
  
  // Default to tomorrow if no start date is configured
  const tomorrow = addDays(today, 1);
  let startDateForDelivery = deliveryStartDate || tomorrow;
  
  // Always use the database date if it exists, otherwise use tomorrow
  if (deliveryStartDate) {
    startDateForDelivery = deliveryStartDate;
    console.log(`Using database start date: ${format(startDateForDelivery, 'yyyy-MM-dd')}`);
  } else {
    startDateForDelivery = tomorrow;
    console.log(`Using tomorrow as start date: ${format(startDateForDelivery, 'yyyy-MM-dd')} (no database date configured)`);
  }

  console.log(`Calculating delivery dates starting from: ${format(startDateForDelivery, 'yyyy-MM-dd')}`);
  
  // Generate 14 days of available delivery dates
  for (let i = 0; i < 14; i++) {
    const date = addDays(startDateForDelivery, i);
    const dayOfWeek = date.getDay();
    
    // Filter slots for this day of week
    let slotsForDay = deliverySlots.filter(slot => slot.day_of_week === dayOfWeek);
    
    // Apply deadline filtering
    slotsForDay = slotsForDay.filter(slot => {
      // If no deadline is set, the slot is available
      if (!slot.order_deadline_days && !slot.order_deadline_time) {
        return true;
      }
      
      // Calculate deadline date and time
      const deadlineDays = slot.order_deadline_days || 0;
      const deadlineTimeStr = slot.order_deadline_time || '23:59';
      
      // Create deadline date (X days before delivery date)
      const deadlineDate = addDays(date, -deadlineDays);
      
      // Set the deadline time
      const [hours, minutes] = deadlineTimeStr.split(':').map(Number);
      deadlineDate.setHours(hours, minutes, 0, 0);
      
      // Check if current time is before deadline
      return !isAfter(now, deadlineDate);
    });
    
    if (slotsForDay.length > 0) {
      dates.push({
        date,
        slots: slotsForDay
      });
    }
  }
  
  if (dates.length > 0) {
    console.log(`Found ${dates.length} available delivery dates, first date: ${format(dates[0].date, 'yyyy-MM-dd')}`);
  } else {
    console.warn('No available delivery dates found in the next 14 days');
  }
  
  return dates;
}

/**
 * Updates the delivery start date in the database (admin only)
 * @param startDate The new start date to set
 * @returns Promise with success status
 */
export async function updateDeliveryStartDate(startDate: string): Promise<boolean> {
  try {
    console.log(`Updating delivery start date to: ${startDate}`);
    
    // Update delivery_settings table (legacy)
    const { error: deliveryError } = await supabase
      .from('delivery_settings')
      .upsert({
        id: 'default',
        start_date: startDate
      });

    if (deliveryError) {
      console.error('Error updating delivery_settings:', deliveryError);
      return false;
    }
    
    // Update app_settings table (primary) - use proper upsert with onConflict
    const { error: appError } = await supabase
      .from('app_settings')
      .upsert({
        key: 'delivery_start_date',
        value: startDate,
        description: 'Start date for delivery calculations'
      }, {
        onConflict: 'key'
      });

    if (appError) {
      console.error('Error updating app_settings:', appError);
      return false;
    }
    
    console.log('Successfully updated delivery start date in both tables');
    return true;
  } catch (error) {
    console.error('Exception in updateDeliveryStartDate:', error);
    return false;
  }
}

/**
 * Automatically selects the earliest available delivery date and time slot
 * @returns Promise with the automatically selected delivery date and slot, or null if none available
 */
export async function getNextAvailableDeliverySlot(): Promise<{ date: Date; slot: DeliverySlot } | null> {
  try {
    console.log("Automatically selecting next available delivery slot");
    
    // Fetch delivery start date and slots
    const [deliveryStartDate, deliverySlots] = await Promise.all([
      fetchDeliveryStartDate(),
      fetchDeliverySlots()
    ]);
    
    if (!deliveryStartDate || deliverySlots.length === 0) {
      console.warn("No delivery configuration available for automatic selection");
      return null;
    }
    
    // Get available delivery dates
    const availableDeliveryDates = calculateAvailableDeliveryDates(deliveryStartDate, deliverySlots);
    
    if (availableDeliveryDates.length === 0) {
      console.warn("No available delivery dates found for automatic selection");
      return null;
    }
    
    // Select the first available date and the earliest time slot for that date
    const firstAvailableDate = availableDeliveryDates[0];
    const earliestSlot = firstAvailableDate.slots.sort((a, b) => a.start_time.localeCompare(b.start_time))[0];
    
    console.log(`Automatically selected delivery: ${format(firstAvailableDate.date, 'yyyy-MM-dd')} at ${earliestSlot.start_time}-${earliestSlot.end_time}`);
    
    return {
      date: firstAvailableDate.date,
      slot: earliestSlot
    };
  } catch (error) {
    console.error('Exception in getNextAvailableDeliverySlot:', error);
    return null;
  }
} 